<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="tc-confirm-backdrop"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="dialogTitleId"
      data-testid="confirm-dialog"
    >
      <div class="tc-confirm-modal">
        <header class="tc-confirm-header">
          <h2 :id="dialogTitleId" class="tc-confirm-title">{{ title }}</h2>
          <button
            type="button"
            class="tc-btn-icon"
            aria-label="Close"
            :disabled="busy"
            @click="onCancel"
          >&times;</button>
        </header>
        <!-- Optional: a dialog whose detail carries its own copy leaves this
             out rather than opening on an empty paragraph. -->
        <p v-if="message" class="tc-confirm-body">{{ message }}</p>
        <!-- What the action will act on, itemised. A confirmation the user has
             to press to understand is not a confirmation (#6239). -->
        <slot name="details" />
        <div class="d-flex justify-content-end gap-2 p-3 pt-0">
          <button
            type="button"
            class="btn btn-outline-secondary btn-sm"
            :disabled="busy"
            @click="onCancel"
          >Cancel</button>
          <!-- The other way to say yes, when there are two. Styled apart from
               Cancel because it acts, and apart from the primary because it is
               the one that overwrites. -->
          <button
            v-if="secondaryLabel"
            type="button"
            class="btn btn-outline-danger btn-sm"
            :disabled="busy || actionsDisabled"
            data-testid="confirm-dialog-secondary"
            @click="onSecondaryClick"
          >{{ secondaryLabel }}</button>
          <button
            type="button"
            :class="['btn', 'btn-sm', destructive ? 'btn-danger' : 'btn-primary']"
            :disabled="busy || actionsDisabled"
            data-testid="confirm-dialog-action"
            @click="onConfirmClick"
          >
            <span v-if="busy" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
            {{ confirmLabel ?? 'Confirm' }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';

const props = defineProps<{
  open: boolean;
  title: string;
  message?: string;
  confirmLabel?: string;
  destructive?: boolean;
  onConfirm: () => void | Promise<void>;
  secondaryLabel?: string;
  onSecondary?: () => void | Promise<void>;
  // Holds the actions while what they would act on is still loading; Cancel stays live.
  actionsDisabled?: boolean;
}>();

const emit = defineEmits<{
  close: [];
}>();

const busy = ref(false);
const dialogTitleId = computed(() => `confirm-dialog-title-${Math.random().toString(36).slice(2)}`);

function onCancel(): void {
  if (busy.value) return;
  emit('close');
}

async function onConfirmClick(): Promise<void> {
  await run(props.onConfirm);
}

async function onSecondaryClick(): Promise<void> {
  if (props.onSecondary) await run(props.onSecondary);
}

async function run(action: () => void | Promise<void>): Promise<void> {
  if (busy.value) return;
  busy.value = true;
  try {
    await action();
  } finally {
    busy.value = false;
    emit('close');
  }
}
</script>
